import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Paper,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Favorite as HeartIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useUserData } from '../../hooks/useUserData';
import { 
  createSurvivorshipPlan, 
  updateSurvivorshipPlan,
  getSurvivorshipPlan
} from '../../services/oncologyService';
import { searchPatients } from '../../services/patientService';
import { SURVEILLANCE_FREQUENCIES, TREATMENT_MODALITIES } from '../../types/enums';
import { PageHeader, ContentCard } from './oncologyStyles';

const SurvivorshipCarePlan = ({ patientId: propPatientId, diagnosisId, existingPlan, onSave, embedded = false }) => {
  const { patientId: urlPatientId, planId } = useParams();
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [patientOptions, setPatientOptions] = useState([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  
  const patientId = propPatientId || urlPatientId || selectedPatient?.id;
  const isEditing = !!planId || !!existingPlan;
  
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    status: existingPlan?.status || 'Draft',
    treatmentSummary: existingPlan?.treatmentSummary || {
      diagnosisSummary: '',
      treatmentsReceived: [],
      completionDate: ''
    },
    surveillanceSchedule: existingPlan?.surveillanceSchedule || [],
    potentialLateEffects: existingPlan?.potentialLateEffects || [],
    healthMaintenance: existingPlan?.healthMaintenance || [],
    followUpProviders: existingPlan?.followUpProviders || [],
    notes: existingPlan?.notes || ''
  });

  // Load existing plan if editing
  useEffect(() => {
    if (planId && defaultFacility?.id) {
      const loadPlan = async () => {
        try {
          setLoadingPlan(true);
          const plan = await getSurvivorshipPlan(planId);
          setFormData({
            status: plan.status || 'Draft',
            treatmentSummary: plan.treatmentSummary || {
              diagnosisSummary: '',
              treatmentsReceived: [],
              completionDate: ''
            },
            surveillanceSchedule: plan.surveillanceSchedule || [],
            potentialLateEffects: plan.potentialLateEffects || [],
            healthMaintenance: plan.healthMaintenance || [],
            followUpProviders: plan.followUpProviders || [],
            notes: plan.notes || ''
          });
          if (plan.patientId) {
            setSelectedPatient({
              id: plan.patientId,
              name: plan.patientName || 'Unknown Patient',
              mrn: plan.patientMRN || ''
            });
          }
        } catch (err) {
          console.error('Error loading survivorship plan:', err);
          setError('Failed to load survivorship plan');
        } finally {
          setLoadingPlan(false);
        }
      };
      loadPlan();
    }
  }, [planId, defaultFacility?.id]);

  // Search patients
  const handlePatientSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2 || !defaultFacility?.id) {
      setPatientOptions([]);
      return;
    }

    try {
      setSearchingPatients(true);
      const results = await searchPatients({
        facilityId: defaultFacility.id,
        search: searchTerm,
        limit: 10
      });
      // Handle both array and object responses
      const patients = Array.isArray(results) ? results : (results?.patients || []);
      setPatientOptions(patients);
    } catch (err) {
      console.error('Error searching patients:', err);
      setPatientOptions([]);
    } finally {
      setSearchingPatients(false);
    }
  }, [defaultFacility?.id]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      handlePatientSearch(patientSearchTerm);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [patientSearchTerm, handlePatientSearch]);

  const handleAddTreatment = () => {
    setFormData(prev => ({
      ...prev,
      treatmentSummary: {
        ...prev.treatmentSummary,
        treatmentsReceived: [
          ...prev.treatmentSummary.treatmentsReceived,
          { modality: '', details: '', startDate: '', endDate: '' }
        ]
      }
    }));
  };

  const handleTreatmentChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      treatmentSummary: {
        ...prev.treatmentSummary,
        treatmentsReceived: prev.treatmentSummary.treatmentsReceived.map((t, i) =>
          i === index ? { ...t, [field]: value } : t
        )
      }
    }));
  };

  const handleAddSurveillance = () => {
    setFormData(prev => ({
      ...prev,
      surveillanceSchedule: [
        ...prev.surveillanceSchedule,
        { testOrExam: '', frequency: '', purpose: '', nextDueDate: '' }
      ]
    }));
  };

  const handleSurveillanceChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      surveillanceSchedule: prev.surveillanceSchedule.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      )
    }));
  };

  const handleAddLateEffect = () => {
    setFormData(prev => ({
      ...prev,
      potentialLateEffects: [
        ...prev.potentialLateEffects,
        { effect: '', risk: 'Medium', monitoring: '', preventionStrategies: [] }
      ]
    }));
  };

  const handleLateEffectChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      potentialLateEffects: prev.potentialLateEffects.map((e, i) =>
        i === index ? { ...e, [field]: value } : e
      )
    }));
  };

  const handleAddProvider = () => {
    setFormData(prev => ({
      ...prev,
      followUpProviders: [
        ...prev.followUpProviders,
        { role: '', name: '', frequency: '' }
      ]
    }));
  };

  const handleProviderChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      followUpProviders: prev.followUpProviders.map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  const handleSubmit = async () => {
    if (!patientId) {
      setError('Please select a patient');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const planData = {
        patientId,
        patientName: selectedPatient?.name || `${selectedPatient?.firstName} ${selectedPatient?.lastName}`,
        patientMRN: selectedPatient?.mrn,
        diagnosisId: diagnosisId || 'general',
        facilityId: defaultFacility.id,
        ...formData
      };

      let result;
      if (planId || existingPlan?.id) {
        result = await updateSurvivorshipPlan(planId || existingPlan.id, planData);
      } else {
        result = await createSurvivorshipPlan(planData);
      }

      setSuccess(true);
      
      if (onSave) {
        onSave(result);
      } else {
        // Navigate back to list after short delay
        setTimeout(() => {
          navigate('/oncology/survivorship');
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving survivorship care plan:', err);
      setError(err.message || 'Failed to save survivorship care plan');
    } finally {
      setLoading(false);
    }
  };

  const surveillanceTests = [
    'Physical examination',
    'Mammogram',
    'CT scan (chest/abdomen/pelvis)',
    'PET scan',
    'MRI',
    'Colonoscopy',
    'PSA test',
    'CA-125',
    'CEA',
    'CBC with differential',
    'Comprehensive metabolic panel',
    'Thyroid function tests',
    'Echocardiogram',
    'Pulmonary function tests',
    'Bone density scan'
  ];

  const lateEffectOptions = [
    'Fatigue',
    'Cognitive changes',
    'Cardiotoxicity',
    'Peripheral neuropathy',
    'Lymphedema',
    'Osteoporosis',
    'Infertility',
    'Secondary malignancy',
    'Hypothyroidism',
    'Pulmonary fibrosis',
    'Chronic pain',
    'Anxiety/Depression'
  ];

  const providerRoles = [
    'Primary Oncologist',
    'Primary Care Physician',
    'Radiation Oncologist',
    'Surgical Oncologist',
    'Cardiologist',
    'Endocrinologist',
    'Physical Therapist',
    'Mental Health Provider',
    'Nutritionist',
    'Social Worker'
  ];

  const planStatuses = ['Draft', 'Active', 'Completed', 'On Hold'];

  const patientSelector = (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PersonIcon color="primary" />
        <Typography variant="subtitle1" fontWeight="bold">
          Patient Selection
        </Typography>
      </Box>
      
      {selectedPatient ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body1" fontWeight="medium">
              {selectedPatient.name || `${selectedPatient.firstName} ${selectedPatient.lastName}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              MRN: {selectedPatient.mrn || 'N/A'}
            </Typography>
          </Box>
          {!isEditing && (
            <Button size="small" onClick={() => setSelectedPatient(null)}>
              Change Patient
            </Button>
          )}
        </Box>
      ) : (
        <Autocomplete
          options={Array.isArray(patientOptions) ? patientOptions : []}
          getOptionLabel={(option) => 
            `${option.firstName || ''} ${option.lastName || ''} (MRN: ${option.mrn || 'N/A'})`
          }
          loading={searchingPatients}
          onInputChange={(e, value) => setPatientSearchTerm(value)}
          onChange={(e, value) => setSelectedPatient(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Patient"
              placeholder="Type patient name or MRN..."
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon color="action" sx={{ ml: 1, mr: 0.5 }} />,
                endAdornment: (
                  <>
                    {searchingPatients ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2">
                  {option.firstName} {option.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  MRN: {option.mrn || 'N/A'} | DOB: {option.dateOfBirth || 'N/A'}
                </Typography>
              </Box>
            </li>
          )}
          noOptionsText={patientSearchTerm.length < 2 ? "Type at least 2 characters" : "No patients found"}
        />
      )}
    </Paper>
  );

  const formContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Survivorship care plan saved successfully!
        </Alert>
      )}

      {/* Status */}
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Plan Status</InputLabel>
          <Select
            value={formData.status}
            label="Plan Status"
            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
          >
            {planStatuses.map(status => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Treatment Summary */}
      <Accordion defaultExpanded sx={{ borderRadius: '8px !important', mb: 2, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">Treatment Summary</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Diagnosis Summary"
                value={formData.treatmentSummary.diagnosisSummary}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  treatmentSummary: { ...prev.treatmentSummary, diagnosisSummary: e.target.value }
                }))}
                placeholder="Brief summary of the cancer diagnosis..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Treatment Completion Date"
                value={formData.treatmentSummary.completionDate ? new Date(formData.treatmentSummary.completionDate) : null}
                onChange={(date) => setFormData(prev => ({
                  ...prev,
                  treatmentSummary: { 
                    ...prev.treatmentSummary, 
                    completionDate: date ? date.toISOString().split('T')[0] : '' 
                  }
                }))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Treatments Received</Typography>
          {formData.treatmentSummary.treatmentsReceived.map((treatment, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 1, p: 1, borderRadius: '6px' }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Modality</InputLabel>
                    <Select
                      value={treatment.modality}
                      label="Modality"
                      onChange={(e) => handleTreatmentChange(index, 'modality', e.target.value)}
                    >
                      {TREATMENT_MODALITIES.map(m => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Details"
                    value={treatment.details}
                    onChange={(e) => handleTreatmentChange(index, 'details', e.target.value)}
                  />
                </Grid>
                <Grid item xs={5} md={2}>
                  <DatePicker
                    label="Start"
                    value={treatment.startDate ? new Date(treatment.startDate) : null}
                    onChange={(date) => handleTreatmentChange(index, 'startDate', date ? date.toISOString().split('T')[0] : '')}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small' }
                    }}
                  />
                </Grid>
                <Grid item xs={5} md={2}>
                  <DatePicker
                    label="End"
                    value={treatment.endDate ? new Date(treatment.endDate) : null}
                    onChange={(date) => handleTreatmentChange(index, 'endDate', date ? date.toISOString().split('T')[0] : '')}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small' }
                    }}
                  />
                </Grid>
                <Grid item xs={2} md={1}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      treatmentSummary: {
                        ...prev.treatmentSummary,
                        treatmentsReceived: prev.treatmentSummary.treatmentsReceived.filter((_, i) => i !== index)
                      }
                    }))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Card>
          ))}
          <Button startIcon={<AddIcon />} onClick={handleAddTreatment} size="small">
            Add Treatment
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Surveillance Schedule */}
      <Accordion sx={{ borderRadius: '8px !important', mb: 2, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">Surveillance Schedule</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {formData.surveillanceSchedule.map((item, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 1, p: 1, borderRadius: '6px' }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={surveillanceTests}
                    value={item.testOrExam}
                    onChange={(e, value) => handleSurveillanceChange(index, 'testOrExam', value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Test/Exam" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={item.frequency}
                      label="Frequency"
                      onChange={(e) => handleSurveillanceChange(index, 'frequency', e.target.value)}
                    >
                      {SURVEILLANCE_FREQUENCIES.map(f => (
                        <MenuItem key={f} value={f}>{f}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Purpose"
                    value={item.purpose}
                    onChange={(e) => handleSurveillanceChange(index, 'purpose', e.target.value)}
                  />
                </Grid>
                <Grid item xs={10} md={3}>
                  <DatePicker
                    label="Next Due"
                    value={item.nextDueDate ? new Date(item.nextDueDate) : null}
                    onChange={(date) => handleSurveillanceChange(index, 'nextDueDate', date ? date.toISOString().split('T')[0] : '')}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small' }
                    }}
                  />
                </Grid>
                <Grid item xs={2} md={1}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      surveillanceSchedule: prev.surveillanceSchedule.filter((_, i) => i !== index)
                    }))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Card>
          ))}
          <Button startIcon={<AddIcon />} onClick={handleAddSurveillance} size="small">
            Add Surveillance Item
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Late Effects */}
      <Accordion sx={{ borderRadius: '8px !important', mb: 2, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">Potential Late Effects</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {formData.potentialLateEffects.map((effect, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 1, p: 1, borderRadius: '6px' }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={lateEffectOptions}
                    value={effect.effect}
                    onChange={(e, value) => handleLateEffectChange(index, 'effect', value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Late Effect" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Risk</InputLabel>
                    <Select
                      value={effect.risk}
                      label="Risk"
                      onChange={(e) => handleLateEffectChange(index, 'risk', e.target.value)}
                    >
                      <MenuItem value="Low">Low</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="High">High</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={10} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Monitoring/Prevention"
                    value={effect.monitoring}
                    onChange={(e) => handleLateEffectChange(index, 'monitoring', e.target.value)}
                  />
                </Grid>
                <Grid item xs={2} md={1}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      potentialLateEffects: prev.potentialLateEffects.filter((_, i) => i !== index)
                    }))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Card>
          ))}
          <Button startIcon={<AddIcon />} onClick={handleAddLateEffect} size="small">
            Add Late Effect
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Follow-up Providers */}
      <Accordion sx={{ borderRadius: '8px !important', mb: 2, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">Follow-up Providers</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {formData.followUpProviders.map((provider, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 1, p: 1, borderRadius: '6px' }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={providerRoles}
                    value={provider.role}
                    onChange={(e, value) => handleProviderChange(index, 'role', value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Role" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Provider Name"
                    value={provider.name}
                    onChange={(e) => handleProviderChange(index, 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={10} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Follow-up Frequency"
                    value={provider.frequency}
                    onChange={(e) => handleProviderChange(index, 'frequency', e.target.value)}
                    placeholder="e.g., Every 3 months"
                  />
                </Grid>
                <Grid item xs={2} md={1}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      followUpProviders: prev.followUpProviders.filter((_, i) => i !== index)
                    }))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Card>
          ))}
          <Button startIcon={<AddIcon />} onClick={handleAddProvider} size="small">
            Add Provider
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Notes */}
      <Box sx={{ mt: 2 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          rows={2}
          label="Additional Notes"
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
        />
      </Box>

      {/* Actions */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/oncology/survivorship')}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={loading || !patientId}
        >
          {loading ? 'Saving...' : (isEditing ? 'Update Plan' : 'Save Care Plan')}
        </Button>
      </Box>
    </>
  );

  // Loading state for edit mode
  if (loadingPlan) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Embedded mode
  if (embedded) {
    return (
      <ContentCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HeartIcon color="success" />
              <Typography variant="h6" fontWeight="bold">
                Survivorship Care Plan
              </Typography>
            </Box>
            <Button startIcon={<PrintIcon />} variant="outlined" size="small">
              Print Plan
            </Button>
          </Box>
          {!propPatientId && patientSelector}
          {formContent}
        </CardContent>
      </ContentCard>
    );
  }

  // Standalone page
  return (
    <Box>
      <PageHeader
        title={isEditing ? 'Edit Survivorship Plan' : 'New Survivorship Plan'}
        subtitle="Post-treatment care and follow-up planning"
        icon={<HeartIcon />}
        backTo="/oncology/survivorship"
      />

      <ContentCard>
        <CardContent>
          {!propPatientId && !urlPatientId && patientSelector}
          {formContent}
        </CardContent>
      </ContentCard>
    </Box>
  );
};

export default SurvivorshipCarePlan;
